/**
 * ConfirmDialog
 * Återanvändbar bekräftelsedialog som ersätter window.confirm()
 * Bygger på BaseModal för konsekvent UX och i18n-stöd
 */

import React, { useState } from 'react';
import { useTranslation } from '@/store/languageStore';
import { BaseModal } from './BaseModal';
import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  loading?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  variant = 'warning',
  loading = false,
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isLoading = loading || isSubmitting;

  const handleConfirm = async () => {
    try {
      setIsSubmitting(true);
      await onConfirm();
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const variantConfig = {
    danger: {
      icon: ExclamationTriangleIcon,
      iconClass: 'text-red-500',
      buttonClass: 'bg-red-600 hover:bg-red-700 text-white',
    },
    warning: {
      icon: ExclamationTriangleIcon,
      iconClass: 'text-amber-500',
      buttonClass: 'bg-amber-600 hover:bg-amber-700 text-white',
    },
    info: {
      icon: InformationCircleIcon,
      iconClass: 'text-blue-500',
      buttonClass: 'bg-primary hover:bg-primary/90 text-white',
    },
  };

  const config = variantConfig[variant];
  const Icon = config.icon;

  const footer = (
    <div className="flex justify-end gap-3">
      <button
        type="button"
        onClick={onClose}
        disabled={isLoading}
        className="px-4 py-2 text-textDefault bg-bgHover rounded-lg hover:bg-bgBase transition-colors disabled:opacity-50"
      >
        {cancelText ?? t('common.cancel') ?? 'Avbryt'}
      </button>
      <button
        type="button"
        onClick={handleConfirm}
        disabled={isLoading}
        className={`flex items-center gap-2 px-6 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${config.buttonClass}`}
      >
        {isLoading && (
          <svg
            className="animate-spin h-4 w-4"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {confirmText ?? t('common.confirm')}
      </button>
    </div>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={footer}
      loading={false}
      closeOnBackdrop={!isLoading}
      showCloseButton={!isLoading}
    >
      <div className="flex items-start gap-4">
        <Icon className={`w-10 h-10 shrink-0 ${config.iconClass}`} />
        <p className="text-textDefault pt-1">{message}</p>
      </div>
    </BaseModal>
  );
};

export default ConfirmDialog;
